import React, { useState } from "react";
import { Search, ChevronRight, X, Eye, Share2, Check, Play, Film } from "lucide-react";
import { StorageService } from "../../services/storage.js";
import { VideoEmbedPlayer } from "../common/VideoEmbedPlayer.js";
import { parseVideoUrl } from "../../utils/media.js";
import { resolveImageUrl } from "../../utils/imageStorage.js";

export const BlogPage = ({
  posts,
  settings,
  lang,
  activePostSlug,
  onClearActivePost,
  pageData
}) => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [activePost, setActivePost] = useState(() => {
    if (activePostSlug) {
      return posts.find((p) => p.slug === activePostSlug) || null;
    }
    return null;
  });
  const [copied, setCopied] = useState(false);
  const categories = ["all", "Academics", "Admissions", "Events", "Islamic Guidance"];
  const publishedPosts = posts.filter((p) => p.status === "published");
  const filteredPosts = publishedPosts.filter((p) => {
    const matchesCategory = selectedCategory === "all" || p.category === selectedCategory;
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !query ||
      p.title.toLowerCase().includes(query) ||
      (p.title_bn && p.title_bn.includes(query)) ||
      (p.content && p.content.toLowerCase().includes(query)) ||
      (p.excerpt && p.excerpt.toLowerCase().includes(query));
    return matchesCategory && matchesSearch;
  });
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);

  const handleOpenPost = (post) => {
    StorageService.incrementPostViews(post.id);
    setActivePost(post);
    setActiveMediaIndex(0);
  };

  const handleCopyLink = () => {
    if (typeof window !== "undefined") {
      navigator.clipboard?.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="py-12 bg-stone-50 min-h-screen space-y-12">
      {/* Page Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-emerald-950/80 backdrop-blur-md text-white rounded-2xl p-8 sm:p-12 relative overflow-hidden shadow-sm">
          {pageData?.hero_image && (
            <div className="absolute inset-0 z-0">
              <img
                src={pageData.hero_image}
                alt="Blog Banner"
                className="w-full h-full object-cover opacity-25"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 bg-emerald-950/65" />
            </div>
          )}
          <div className="max-w-3xl space-y-3 relative z-10">
            <span className="text-amber-400 font-bold text-xs uppercase tracking-widest block font-sans">
              {lang === "bn" ? pageData?.title_bn || "মাদ্রাসার ব্লগ, খবর ও বিজ্ঞপ্তি" : pageData?.title || "Blog, Articles & Announcements"}
            </span>
            <h1 className="text-3xl sm:text-5xl font-serif font-bold tracking-tight text-balance">
              {lang === "bn" ? pageData?.subtitle_bn || "দ্বীনি শিক্ষা ও জ্ঞানকোষ" : pageData?.subtitle || "Reflections & Academic Circulars"}
            </h1>
            <p className="text-sm sm:text-base text-emerald-100/90 leading-relaxed font-sans">
              {lang === "bn" ? pageData?.content_bn || "আদর্শ শিশু প্রতিপালন, সহীহ কুরআন চর্চা, মাদ্রাসার কার্যক্রম ও পরীক্ষার সর্বশেষ তথ্যাবলী।" : pageData?.content || "Scholastic essays, event recaps, character building guides, and official notices from our administration."}
            </p>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-1.5 w-full md:w-auto">
            {categories.map((cat) => {
              const isActive = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer ${
                    isActive ? "bg-emerald-900 text-white shadow-xs" : "text-slate-600 bg-stone-100 hover:bg-stone-200"
                  }`}
                >
                  {cat === "all" ? (lang === "bn" ? "সকল" : "All") : cat}
                </button>
              );
            })}
          </div>

          <div className="relative w-full md:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={lang === "bn" ? "প্রবন্ধ বা বিষয় খুঁজুন..." : "Search articles or topics..."}
              className="w-full pl-9 pr-4 py-2 text-xs rounded-lg border border-stone-200 bg-stone-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-700"
            />
          </div>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {filteredPosts.length === 0 ? (
          <div className="bg-white p-12 text-center rounded-xl border border-stone-200">
            <p className="text-slate-500 text-sm">
              {lang === "bn" ? "কোনো পোস্ট পাওয়া যায়নি।" : "No articles match your search criteria."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post) => (
              <article
                key={post.id}
                onClick={() => handleOpenPost(post)}
                className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-xs hover:shadow-md transition-all cursor-pointer flex flex-col group"
              >
                <div className="h-52 overflow-hidden bg-stone-100 relative">
                  <img
                    src={resolveImageUrl(post.cover_image)}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    referrerPolicy="no-referrer"
                  />
                  <div className="absolute top-3 left-3 bg-emerald-950/80 text-amber-300 text-[10px] font-bold px-2.5 py-0.5 rounded tracking-wide backdrop-blur-xs flex items-center gap-1.5">
                    <span>{post.category}</span>
                    {post.media && post.media.length > 1 && (
                      <span className="bg-amber-400 text-emerald-950 px-1.5 py-0.2 rounded text-[9px] font-bold">
                        +{post.media.length - 1} photos
                      </span>
                    )}
                    {(Boolean(post.video_url) || post.media?.some((m) => m.type === "video")) && (
                      <span className="bg-red-600 text-white px-1.5 py-0.2 rounded text-[9px] font-bold flex items-center gap-0.5">
                        <Film className="w-2.5 h-2.5" />
                        <span>Video</span>
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2.5">
                      <span>{post.author_name}</span>
                      <span aria-hidden="true">·</span>
                      <span>{post.created_at ? post.created_at.substring(0, 10) : ""}</span>
                      <span aria-hidden="true">·</span>
                      <span>{post.read_time}</span>
                    </div>

                    <h3 className="text-lg font-bold text-slate-900 group-hover:text-emerald-800 transition-colors line-clamp-2">
                      {lang === "bn" ? post.title_bn : post.title}
                    </h3>

                    <p className="mt-2.5 text-xs text-slate-600 line-clamp-3 leading-relaxed">
                      {lang === "bn" ? post.excerpt_bn : post.excerpt}
                    </p>
                  </div>

                  <div className="mt-5 pt-3 border-t border-stone-100 flex items-center justify-between text-xs font-semibold text-emerald-800">
                    <span className="flex items-center gap-1 group-hover:text-emerald-950">
                      <span>{lang === "bn" ? "সম্পূর্ণ পড়ুন" : "Read Article"}</span>
                      <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                    <span className="text-[11px] text-slate-400 font-normal flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {post.views} views
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* Reader Modal */}
      {activePost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl overflow-y-auto border border-stone-200 flex flex-col">
            <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-md px-6 py-4 border-b border-stone-200 flex items-center justify-between">
              <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider">
                {activePost.category}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyLink}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-stone-100 transition-colors cursor-pointer"
                  title="Copy link"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Share2 className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => {
                    setActivePost(null);
                    if (onClearActivePost) onClearActivePost();
                  }}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-stone-100 transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 sm:p-8 space-y-6">
              {(() => {
                const mediaList = [];
                if (activePost.video_url) {
                  mediaList.push({
                    id: "feat-video",
                    type: "video",
                    url: activePost.video_url,
                    caption: activePost.title
                  });
                }
                if (activePost.media && activePost.media.length > 0) {
                  activePost.media.forEach((m) => {
                    if (!mediaList.some((item) => item.url === m.url)) {
                      mediaList.push(m);
                    }
                  });
                } else if (activePost.cover_image && !mediaList.some((item) => item.url === activePost.cover_image)) {
                  mediaList.push({
                    id: "m-default",
                    type: "image",
                    url: activePost.cover_image,
                    caption: activePost.title
                  });
                }
                const currentItem = mediaList[activeMediaIndex] || mediaList[0] || {
                  id: "fallback",
                  type: "image",
                  url: activePost.cover_image,
                  caption: activePost.title
                };
                return (
                  <div className="space-y-3">
                    <div className="h-64 sm:h-96 rounded-xl overflow-hidden bg-slate-950 border border-stone-200 relative flex items-center justify-center shadow-inner">
                      {currentItem.type === "video" ? (
                        <div className="w-full h-full flex items-center justify-center bg-black">
                          <VideoEmbedPlayer
                            url={currentItem.url}
                            title={currentItem.caption || activePost.title}
                            className="w-full h-full"
                          />
                        </div>
                      ) : (
                        <img
                          src={resolveImageUrl(currentItem.url)}
                          alt={currentItem.caption || activePost.title}
                          className="w-full h-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      )}

                      <div className="absolute top-3 left-3 bg-black/75 text-white text-[10px] font-bold px-2 py-0.5 rounded-full backdrop-blur-xs flex items-center gap-1 z-10 pointer-events-none">
                        {currentItem.type === "video" ? (
                          <>
                            <Film className="w-3 h-3 text-amber-400" />
                            <span>
                              {parseVideoUrl(currentItem.url).platform === "youtube"
                                ? "YouTube Video"
                                : parseVideoUrl(currentItem.url).platform === "facebook"
                                ? "Facebook Video"
                                : "Video"}
                            </span>
                          </>
                        ) : (
                          <span>📸 Photo {activeMediaIndex + 1} of {mediaList.length}</span>
                        )}
                      </div>
                    </div>

                    {currentItem.caption && (
                      <p className="text-xs text-slate-500 italic text-center">
                        {currentItem.caption}
                      </p>
                    )}

                    {mediaList.length > 1 && (
                      <div className="flex items-center gap-2 overflow-x-auto py-2">
                        {mediaList.map((item, idx) => {
                          const isVid = item.type === "video";
                          const vInfo = isVid ? parseVideoUrl(item.url) : null;
                          const thumb = vInfo?.thumbnailUrl;
                          return (
                            <button
                              key={item.id}
                              onClick={() => setActiveMediaIndex(idx)}
                              className={`w-16 h-12 rounded-lg overflow-hidden border-2 shrink-0 transition-all cursor-pointer relative ${
                                activeMediaIndex === idx
                                  ? "border-amber-500 ring-2 ring-amber-400/40 scale-105"
                                  : "border-stone-200 opacity-60 hover:opacity-100"
                              }`}
                            >
                              {isVid ? (
                                thumb ? (
                                  <div className="relative w-full h-full">
                                    <img src={thumb} alt="" className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                      <Play className="w-3.5 h-3.5 text-white fill-white" />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="w-full h-full bg-slate-900 flex flex-col items-center justify-center text-white text-[9px] font-mono p-1">
                                    <Film className="w-3.5 h-3.5 text-amber-400 mb-0.5" />
                                    <span className="truncate max-w-full text-[8px]">
                                      {vInfo?.platform === "facebook" ? "FB" : "VID"}
                                    </span>
                                  </div>
                                )
                              ) : (
                                <img
                                  src={item.url}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  referrerPolicy="no-referrer"
                                />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div>
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                  <span>Written by {activePost.author_name}</span>
                  <span aria-hidden="true">·</span>
                  <span>{activePost.created_at}</span>
                  <span aria-hidden="true">·</span>
                  <span>{activePost.read_time}</span>
                </div>

                <h1 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900 leading-snug">
                  {lang === "bn" ? activePost.title_bn : activePost.title}
                </h1>
              </div>

              <div className="prose prose-slate max-w-none text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-line font-sans">
                {lang === "bn" ? activePost.content_bn : activePost.content}
              </div>

              <div className="pt-6 border-t border-stone-200 flex items-center justify-between text-xs text-slate-500">
                <span>Baitul Muqaddas Model Madrasah Blog</span>
                <button
                  onClick={() => {
                    setActivePost(null);
                    if (onClearActivePost) onClearActivePost();
                  }}
                  className="text-emerald-800 font-semibold hover:underline cursor-pointer"
                >
                  Close & Return to List
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
